import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Clock In/Out Status Interface
 * Represents the current clock status for a staff member
 */
export interface ClockStatus {
  /** Whether the staff member is currently clocked in */
  is_clocked_in: boolean;

  /** The time when the staff member clocked in (null if clocked out) */
  clock_in?: Date | string | null;

  /** The ID of the staff member this status belongs to */
  staff_id?: number;
}

/**
 * Clock In Service
 *
 * Centralized service to manage and broadcast clock in/out status changes
 * across multiple components in real-time using RxJS BehaviorSubject.
 *
 * ## Purpose
 * - Synchronizes clock in/out state between header menu and staff detail page
 * - Provides instant UI updates across all subscribed components
 * - Supports optimistic UI updates with error rollback
 *
 * ## Usage
 * ```typescript
 * // In component constructor
 * constructor(private clockInService: ClockInService) {}
 *
 * // Subscribe to clock status updates
 * this.clockInService.clockStatus$.subscribe(status => {
 *   if (status && status.staff_id === this.staffId) {
 *     this.isCurrentlyClockedIn = status.is_clocked_in;
 *   }
 * });
 *
 * // Update clock status after API call
 * this.clockInService.updateClockStatus({
 *   is_clocked_in: true,
 *   clock_in: new Date(),
 *   staff_id: 35
 * });
 * ```
 *
 * ## Architecture
 * - Uses BehaviorSubject to maintain current state and emit to new subscribers
 * - Components update local state optimistically before API call
 * - On API success: Confirm optimistic update (already done)
 * - On API failure: Rollback optimistic update to previous state
 *
 * @author PS ERP Frontend Team
 * @version 3.0
 */
@Injectable({
  providedIn: 'root'
})
export class ClockInService {

  /**
   * BehaviorSubject to hold and broadcast the current clock status
   * BehaviorSubject is used instead of Subject because:
   * - It stores the last emitted value
   * - New subscribers immediately receive the current value
   * - Ensures components always have the latest state
   */
  private clockStatusSubject = new BehaviorSubject<ClockStatus | null>(null);

  /**
   * Observable stream that components subscribe to for real-time updates
   * Components should subscribe to this in ngOnInit and unsubscribe in ngOnDestroy
   */
  public clockStatus$: Observable<ClockStatus | null> = this.clockStatusSubject.asObservable();

  constructor() {}

  /**
   * Get the current clock status value synchronously
   *
   * @returns Current clock status or null if no status has been set
   *
   * @example
   * ```typescript
   * const currentStatus = this.clockInService.getCurrentStatus();
   * if (currentStatus?.is_clocked_in) {
   *   console.log('User is clocked in');
   * }
   * ```
   */
  getCurrentStatus(): ClockStatus | null {
    return this.clockStatusSubject.value;
  }

  /**
   * Update clock status and broadcast to all subscribers immediately
   *
   * This method should be called:
   * 1. Immediately when user clicks clock in/out (optimistic update)
   * 2. After successful API response (confirm with actual server time)
   * 3. On API error (rollback to previous state)
   *
   * @param status - New clock status to broadcast
   *
   * @example
   * ```typescript
   * // Optimistic update before API call
   * this.clockInService.updateClockStatus({
   *   is_clocked_in: true,
   *   clock_in: new Date(),
   *   staff_id: this.staffId
   * });
   *
   * // API call
   * this.api.clockIn(staffId).subscribe({
   *   next: (response) => {
   *     // Confirm with actual server time
   *     this.clockInService.updateClockStatus({
   *       is_clocked_in: true,
   *       clock_in: response.data.clock_in,
   *       staff_id: this.staffId
   *     });
   *   },
   *   error: () => {
   *     // Rollback optimistic update
   *     this.clockInService.updateClockStatus({
   *       is_clocked_in: false,
   *       clock_in: null,
   *       staff_id: this.staffId
   *     });
   *   }
   * });
   * ```
   */
  updateClockStatus(status: ClockStatus): void {
    this.clockStatusSubject.next(status);
  }

  /**
   * Clear clock status (reset to null)
   *
   * Should be called when:
   * - User logs out
   * - Session expires
   * - Navigating away from clock-enabled contexts
   *
   * @example
   * ```typescript
   * // On logout
   * this.clockInService.clearClockStatus();
   * ```
   */
  clearClockStatus(): void {
    this.clockStatusSubject.next(null);
  }
}
